import React from 'react';
import { Box, Typography, Tooltip, IconButton, Theme, FormControl } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { MFTextField } from '../../../lib/formik';
import { getIn } from 'formik';

interface FormFieldConfig {
    label: string;
    key: string;
    namePath: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    error?: boolean;
    numeric?: boolean;
    readOnly?: boolean;
    placeholder?: string;
}

interface MobileInvestmentFormCardProps {
    instrument: string;
    fields: FormFieldConfig[];
    remarks?: string;
    remarksError?: boolean;
    onRemarksClick?: () => void;
}

export default function MobileInvestmentFormCard({
    instrument,
    fields,
    remarks,
    remarksError,
    onRemarksClick,
}: MobileInvestmentFormCardProps): JSX.Element {
    const hasRemark = Boolean(String(remarks || '').trim());

    return (
        <Box
            sx={(theme: Theme) => ({
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px',
                backgroundColor: theme.palette.common.white,
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
                width: '100%',
                boxSizing: 'border-box',
            })}
        >
       
            <Typography
                sx={(theme: Theme) => ({
                    fontSize: '16px',
                    fontWeight: 600,
                    color: theme.palette.primary[900],
                    marginBottom: '12px',
                })}
            >
                {instrument}
            </Typography>

          {/* //dynamic render */}
            {fields.map((field, index) => (
                <Box key={field.key || index} sx={{ marginBottom: '12px' }}>
                    <Typography
                        sx={(theme: Theme) => ({
                            fontSize: '12px',
                            fontWeight: 500,
                            color: theme.palette.text.secondary,
                            marginBottom: '6px',
                        })}
                    >
                        {field.label}
                    </Typography>
                    <FormControl fullWidth error={field.error} variant="standard" sx={{ width: '100%' }}>
                        <MFTextField
                            name={field.namePath}
                            value={field.value}
                            onChange={field.onChange}
                            size="small"
                            fullWidth
                            placeholder={field.placeholder || field.label}
                            error={field.error}
                            inputProps={
                                field.readOnly
                                    ? { readOnly: true }
                                    : field.numeric
                                        ? { inputMode: 'numeric', pattern: '[0-9]*' }
                                        : undefined
                            }
                            sx={
                                field.readOnly
                                    ? {
                                        '& .MuiInputBase-root': {
                                            backgroundColor: '#f5f5f5',
                                            '&.Mui-focused': {
                                                backgroundColor: '#f5f5f5',
                                            },
                                            '& .MuiInputBase-input': {
                                                cursor: 'default',
                                                color: 'text.secondary',
                                            },
                                        },
                                    }
                                    : {}
                            }
                        />
                    </FormControl>
                </Box>
            ))}

            {remarks !== undefined && (
                <Box sx={{ marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography
                        sx={(theme: Theme) => ({
                            fontSize: '12px',
                            fontWeight: 500,
                            color: theme.palette.text.secondary,
                        })}
                    >
                        Remarks
                    </Typography>
                    <Tooltip title={hasRemark ? 'Edit remark' : 'Add a remark'} placement="top" arrow>
                        <IconButton
                            size="small"
                            onClick={onRemarksClick}
                            disabled={!onRemarksClick}
                            sx={{
                                color: hasRemark ? '#ffb74d' : 'inherit',
                            }}
                        >
                            {hasRemark ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
}

